import { useMemo, useState } from "react"
import { ArrowCounterClockwise } from "@phosphor-icons/react"

import {
  Badge,
  Button,
  Checkbox,
  IconButton,
  NumericInput,
  Select,
  type SelectOption,
} from "~/components/design-system/components"
import SearchField from "~/components/planning/SearchField"
import { DialogContent, DialogRoot, DialogTitle } from "~/components/reusable/Dialog"
import HeaderTabs from "~/components/reusable/HeaderTabs"
import ProductImage from "~/components/reusable/ProductImage"
import { Tooltip } from "~/components/reusable/Tooltip"
import {
  QTY_BOUNDS,
  SETTING_FIELDS,
  storeSkuKey,
  type QtyField,
  type SettingField,
  type StoreSkuCell,
} from "~/utils/allocationLineSettings"
import { cn } from "~/utils/helpers"
import { pluralize } from "~/utils/formatting"
import type { StoreWithStatus } from "../../useAllocationData"
import {
  ALL_STORES_SCOPE,
  fillVariantField,
  fillScopeOptions,
  inheritedValue,
  stageStoreSkuField,
  scopeLabel,
  stageVariantField,
  storeSkuRowOf,
  storesInScope,
  variantDraftChangeCount,
  variantRowOf,
  VIEW_SCOPE,
  type DialogView,
  type ProductSettingsDraft,
  type SavedProductSettings,
  type VariantDraft,
} from "./productSettingsModel"
import { fieldOfTab, formatUnits, SETTING_FIELD_META, type DialogTab } from "./productSettingFields"
import type { ProductListing, useProductSettingsDraft } from "./useProductSettings"

const TAB_DESCRIPTION: Record<SettingField, string> = {
  excluded:
    "Unchecked cells stop that size reaching that store. Stock already there stays where it is.",
  minDisplayQty:
    "The fewest units the store shows of that size, even with no sales. Replenishment tops the store up to it. An empty cell follows the all stores value, or no minimum.",
  maxQty:
    "The most stock the store may hold of that size: on hand, in transit and the suggested qty together. The suggested qty is cut to fit under it. It is not the overstock warning. An empty cell follows the all stores value, or no maximum.",
  orderMultiple:
    "A suggested qty is rounded up to the nearest multiple of this number for that store and size. An empty cell follows the all stores value, or 1.",
}

const TABS = SETTING_FIELDS.map((field) => ({
  key: SETTING_FIELD_META[field].tab,
  label: SETTING_FIELD_META[field].tabLabel,
  description: TAB_DESCRIPTION[field],
}))

function showQty(value: number | null) {
  return value === null ? EMPTY : `${value}`
}

const MIN_STORE_COL = 160
const RESET_COL = 32
/** Few sizes get roomy columns; many get narrow ones, and past the dialog's width the grid scrolls. */
const qtyColWidth = (sizeCount: number) => (sizeCount <= 3 ? 112 : 64)

const CELL_INPUT = "rounded-md bg-base px-1 text-center tabular-nums"
const EMPTY = "—"
/** A cell with no value and nothing to inherit draws a dashed outline instead of a filled box. */
const EMPTY_INPUT = "border border-dashed border-strong bg-transparent not-focus:shadow-none"
/** A value staged in this dialog and not saved yet. */
const EDITED_INPUT =
  "border border-badge-blue-border bg-badge-blue-bg font-semibold text-badge-blue-fg-text placeholder:text-badge-blue-fg-text not-focus:shadow-none"

/** Why a quantity cell's input reads as it does, when that isn't plain. */
function cellTitle(excluded: boolean, shown: number | null | undefined): string | undefined {
  if (excluded) return "Excluded at this store"
  if (shown === undefined) return "The sizes differ"
  return undefined
}

/** A number shared by every value, or undefined when they differ. */
function common<T>(values: T[]): T | undefined {
  return values.every((value) => value === values[0]) ? values[0] : undefined
}

type SettingsDraft = ReturnType<typeof useProductSettingsDraft>

type GridProps = {
  tab: DialogTab
  /** With scope All stores and no search, the Fill a column x All sizes cell holds the variant's own value. */
  variantFill?: { input: React.ReactNode; reset?: () => void }
  listing: ProductListing
  sizes: ProductListing["skus"]
  /** The stores in the fill scope: the rows, and what a Fill a column value writes to. */
  stores: StoreWithStatus[]
  saved: SavedProductSettings
  entry: VariantDraft | undefined
  stage: (cells: StoreSkuCell[], value: number | boolean | null) => void
}

function ResetButton({
  label,
  disabled,
  onClick,
}: {
  label: string
  disabled: boolean
  onClick: () => void
}) {
  return (
    <IconButton
      size="2xs"
      variant="transparent"
      requires="edit"
      disabled={disabled}
      aria-label={label}
      allowedTitle={disabled ? `${label}: nothing to reset` : label}
      onClick={onClick}
    >
      <ArrowCounterClockwise />
    </IconButton>
  )
}

function QtyInput({
  field,
  own,
  placeholder,
  edited,
  dimmed,
  title,
  onCommit,
}: {
  field: QtyField
  own: number | null
  placeholder: string
  edited: boolean
  dimmed?: boolean
  title?: string
  onCommit: (value: number | null) => void
}) {
  const bounds = QTY_BOUNDS[field]
  return (
    <NumericInput
      value={own ?? ""}
      placeholder={placeholder}
      onCommit={onCommit}
      min={bounds.min}
      max={bounds.max}
      allowZero={bounds.min === 0}
      small
      requires="edit"
      allowedTitle={title}
      className={cn(
        CELL_INPUT,
        edited && EDITED_INPUT,
        !edited && own === null && placeholder === EMPTY && EMPTY_INPUT,
      )}
      containerClassName={cn("w-14", dimmed && "opacity-50")}
    />
  )
}

/** The Fill a column input: a value fills the column for the stores in scope, then the field empties. */
function FillInput({
  field,
  disabled,
  onFill,
}: {
  field: QtyField
  disabled: boolean
  onFill: (value: number) => void
}) {
  const [resetKey, setResetKey] = useState(0)
  const bounds = QTY_BOUNDS[field]
  return (
    <NumericInput
      key={resetKey}
      value=""
      placeholder={EMPTY}
      disabled={disabled}
      onCommit={(value) => {
        if (value !== null) onFill(value)
        setResetKey((key) => key + 1)
      }}
      min={bounds.min}
      max={bounds.max}
      allowZero={bounds.min === 0}
      small
      requires="edit"
      className={cn(CELL_INPUT, EMPTY_INPUT)}
      containerClassName="w-14"
    />
  )
}

function listingCells(listing: ProductListing): StoreSkuCell[] {
  return listing.skus.flatMap((sku) =>
    sku.locationIds.map((locationId) => ({ skuId: sku.skuId, locationId })),
  )
}

function totalOf(values: (number | null)[]): number | null {
  return values.some((value) => value === null)
    ? null
    : values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
}

function SettingsGrid({
  tab,
  variantFill,
  listing,
  sizes,
  stores,
  saved,
  entry,
  stage,
}: GridProps) {
  const variantRow = variantRowOf(saved, listing.variantId, entry)
  const field = fieldOfTab(tab)
  const cellOf = (store: StoreWithStatus, skuId: string): StoreSkuCell => ({
    skuId,
    locationId: store.allocationLocationId,
  })
  const columnCells = (skuIds: string[], over: StoreWithStatus[]) =>
    over.flatMap((store) => skuIds.map((skuId) => cellOf(store, skuId)))
  const allSkuIds = listing.skus.map((sku) => sku.skuId)
  const rowOf = (cell: StoreSkuCell) => storeSkuRowOf(saved, cell, entry)
  const isEdited = (cell: StoreSkuCell) => entry?.cells[storeSkuKey(cell)]?.[field] !== undefined
  const isExcluded = (cell: StoreSkuCell) => rowOf(cell)?.excluded === true
  const showTotal = tab === "minDisplayQty" || tab === "maxQty"
  const storeTotal = (store: StoreWithStatus) => {
    if (tab === "stores") return null
    const values = sizes
      .map((sku) => cellOf(store, sku.skuId))
      .filter((cell) => !isExcluded(cell))
      .map((cell) => {
        const row = rowOf(cell)
        return row?.[tab] ?? inheritedValue(row, variantRow, tab)
      })
    return totalOf(values)
  }

  const headerCell =
    "paragraph-sm sticky top-0 z-10 border-r border-b border-base bg-subtle px-1 py-2 text-left font-medium whitespace-nowrap text-fg-subtle"
  // Quantity cells pad 4px a side, so a 56px input fits a 64px column; the store column pads 8px.
  const bodyCell = "border-r border-b border-base px-1 py-1"
  const storeCol = "px-2"

  const fillCell = (skuIds: string[]) => {
    const cells = columnCells(skuIds, stores)
    if (tab === "stores") {
      const included = cells.filter((cell) => !isExcluded(cell)).length
      return (
        <Checkbox
          requires="edit"
          disabled={cells.length === 0}
          checked={cells.length > 0 && included === cells.length}
          indeterminate={included > 0 && included < cells.length}
          aria-label="Include in every store in scope"
          onCheckedChange={(checked) => stage(cells, checked !== true)}
        />
      )
    }
    return (
      <FillInput
        field={tab}
        disabled={cells.length === 0}
        onFill={(value) => stage(cells, value)}
      />
    )
  }

  const storeCell = (store: StoreWithStatus, skuIds: string[]) => {
    const cells = columnCells(skuIds, [store])
    if (cells.length === 0) {
      return (
        <span className="paragraph-sm text-fg-muted" title="No package sends this size here">
          {EMPTY}
        </span>
      )
    }
    const edited = cells.some(isEdited)
    if (tab === "stores") {
      const included = cells.filter((cell) => !isExcluded(cell)).length
      return (
        <Checkbox
          requires="edit"
          checked={included === cells.length}
          indeterminate={included > 0 && included < cells.length}
          className={cn(edited && "shadow-borders-interactive-with-shadow")}
          aria-label={`Include at ${store.storeNumber}`}
          onCheckedChange={(checked) => stage(cells, checked !== true)}
        />
      )
    }
    const rows = cells.map(rowOf)
    const own = common(rows.map((row) => row?.[tab] ?? null)) ?? null
    // A cell that follows a variant value staged in this dialog shows as changed too.
    const followsStaged = own === null && entry?.variant?.[tab] !== undefined
    const changed = edited || followsStaged
    // What each size runs on; an empty All sizes cell shows it only when every size agrees.
    const shown = common(rows.map((row) => row?.[tab] ?? inheritedValue(row, variantRow, tab)))
    const excluded = cells.every(isExcluded)
    return (
      <QtyInput
        field={tab}
        own={own}
        placeholder={shown === undefined ? EMPTY : showQty(shown)}
        edited={changed}
        dimmed={excluded}
        title={cellTitle(excluded, shown)}
        onCommit={(value) => stage(cells, value)}
      />
    )
  }

  // One reset per store row clears every size's own value on this tab, so the row follows all stores again.
  const rowReset = (store: StoreWithStatus) => {
    const cells = columnCells(allSkuIds, [store])
    const hasOwn = cells.some((cell) => (rowOf(cell)?.[field] ?? null) !== null)
    return (
      <ResetButton
        label={`Reset ${store.storeNumber} to the all stores value`}
        disabled={!hasOwn}
        onClick={() => stage(cells, null)}
      />
    )
  }

  const subtle = "bg-subtle"
  // The store column takes what the quantity columns leave.
  const qtyCol = qtyColWidth(sizes.length)
  const qtyCols = sizes.length + 1 + (showTotal ? 1 : 0)
  return (
    <table
      className="w-full table-fixed border-separate border-spacing-0"
      style={{ minWidth: MIN_STORE_COL + qtyCol * qtyCols + RESET_COL }}
    >
      <colgroup>
        <col />
        {Array.from({ length: qtyCols }, (_, index) => (
          <col key={index} style={{ width: qtyCol }} />
        ))}
        <col style={{ width: RESET_COL }} />
      </colgroup>
      <thead>
        <tr>
          <th className={cn(headerCell, storeCol, "left-0 z-20")}>Store</th>
          <th
            className={cn(headerCell, "bg-background text-center leading-tight whitespace-normal")}
          >
            All sizes
          </th>
          {sizes.map((sku) => (
            <th key={sku.skuId} className={cn(headerCell, "text-center")}>
              {sku.size}
            </th>
          ))}
          {showTotal && <th className={cn(headerCell, "bg-background text-center")}>Total</th>}
          <th className={headerCell} aria-label="Reset" />
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className={cn(bodyCell, storeCol, "sticky left-0 z-10", subtle)}>
            <span className="paragraph-sm font-medium">Fill a column</span>
            <span className="paragraph-xs ml-2 text-fg-muted">
              {pluralize(stores.length, "store")} in scope
            </span>
          </td>
          <td className={cn(bodyCell, subtle)}>
            <span className="flex justify-center">
              {variantFill ? variantFill.input : fillCell(allSkuIds)}
            </span>
          </td>
          {sizes.map((sku) => (
            <td key={sku.skuId} className={cn(bodyCell, subtle)}>
              <span className="flex justify-center">{fillCell([sku.skuId])}</span>
            </td>
          ))}
          {showTotal && (
            <td className={cn(bodyCell, subtle, "label-sm text-center tabular-nums")}>
              {formatUnits(totalOf(stores.map(storeTotal)))}
            </td>
          )}
          <td className={cn(bodyCell, subtle)}>
            {variantFill && (
              <ResetButton
                label="Reset the all stores value"
                disabled={variantFill.reset === undefined}
                onClick={() => variantFill.reset?.()}
              />
            )}
          </td>
        </tr>
        {stores.map((store) => (
          <tr key={store.id}>
            <td className={cn(bodyCell, storeCol, "sticky left-0 z-10 bg-base")}>
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="paragraph-sm truncate font-medium">{store.storeNumber}</span>
                <span className="paragraph-xs shrink-0 text-fg-muted">
                  {scopeLabel(store.storeClass)}
                </span>
              </span>
            </td>
            <td className={cn(bodyCell, subtle)}>
              <span className="flex justify-center">{storeCell(store, allSkuIds)}</span>
            </td>
            {sizes.map((sku) => (
              <td key={sku.skuId} className={bodyCell}>
                <span className="flex justify-center">{storeCell(store, [sku.skuId])}</span>
              </td>
            ))}
            {showTotal && (
              <td className={cn(bodyCell, subtle, "label-sm text-center tabular-nums")}>
                {formatUnits(storeTotal(store))}
              </td>
            )}
            <td className={bodyCell}>{rowReset(store)}</td>
          </tr>
        ))}
        {stores.length === 0 && (
          <tr>
            <td
              colSpan={sizes.length + (showTotal ? 4 : 3)}
              className="paragraph-sm px-3 py-6 text-fg-muted"
            >
              No store matches the search and fill scope.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

/** The variant's own value, edited in the Fill a column x All sizes cell when it reaches every store. */
function variantFillControl(
  field: QtyField,
  listing: ProductListing,
  saved: SavedProductSettings,
  settingsDraft: SettingsDraft,
  cells: StoreSkuCell[],
): NonNullable<GridProps["variantFill"]> {
  const entry = settingsDraft.draft[listing.variantId]
  const own = variantRowOf(saved, listing.variantId, entry)?.[field] ?? null
  const edited = entry?.variant?.[field] !== undefined
  const commit = (value: number | null) =>
    settingsDraft.update((current, savedNow) =>
      value === null
        ? stageVariantField(current, savedNow, listing.variantId, field, null)
        : fillVariantField(current, savedNow, listing.variantId, field, value, cells),
    )
  return {
    input: (
      <QtyInput
        field={field}
        own={own}
        placeholder={EMPTY}
        edited={edited}
        title="Fills every store and size, and stores added later. Empty uses the default."
        onCommit={commit}
      />
    ),
    reset: own !== null ? () => commit(null) : undefined,
  }
}

/** One variant's settings per store and size, staged into the page's Commandbar. */
export default function ProductSettingsDialog({
  listing,
  view,
  stores,
  saved,
  settingsDraft,
  draftAtOpen,
  tab,
  onTabChange,
  onClose,
}: {
  listing: ProductListing
  /** The table's view it was opened from: the Current view fill scope. */
  view?: DialogView
  stores: StoreWithStatus[]
  saved: SavedProductSettings
  settingsDraft: SettingsDraft
  /** What Cancel, Escape and × put back. */
  draftAtOpen: ProductSettingsDraft
  tab: DialogTab
  onTabChange: (tab: DialogTab) => void
  onClose: () => void
}) {
  const [storeSearch, setStoreSearch] = useState("")
  const [scope, setScope] = useState(view ? VIEW_SCOPE : ALL_STORES_SCOPE)

  const variantStores = useMemo(() => {
    const storeById = new Map(stores.map((store) => [store.id, store]))
    return listing.storeIds.flatMap((id) => storeById.get(id) ?? [])
  }, [stores, listing.storeIds])
  const fillScopes = useMemo<SelectOption[]>(
    () => fillScopeOptions(variantStores, view),
    [view, variantStores],
  )

  const needle = storeSearch.toLowerCase()
  const visibleStores = variantStores.filter((store) =>
    store.storeNumber.toLowerCase().includes(needle),
  )
  const fillStores = storesInScope(visibleStores, scope, view)

  const field = fieldOfTab(tab)
  const stage = (cells: StoreSkuCell[], value: number | boolean | null) =>
    settingsDraft.update((current, savedNow) =>
      stageStoreSkuField(current, savedNow, listing.variantId, cells, field, value),
    )

  const entry = settingsDraft.draft[listing.variantId]
  const variantChangeCount = variantDraftChangeCount(entry)
  const activeTab = TABS.find((t) => t.key === tab) ?? TABS[0]!
  const allCells = listingCells(listing)

  const cancel = () => {
    settingsDraft.update(() => draftAtOpen)
    onClose()
  }

  return (
    <DialogRoot open onOpenChange={(open) => !open && cancel()}>
      <DialogContent className="flex h-[80vh] max-h-200 max-w-4xl flex-col gap-0 p-0">
        <div className="flex shrink-0 items-center gap-3 px-5 pt-4 pb-2 pr-12">
          <ProductImage url={listing.imageUrl} alt={listing.name} width={40} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="flex items-center gap-2">
              <DialogTitle className="truncate">{listing.productName}</DialogTitle>
              <Badge size="sm" variant="gray">
                Variant settings
              </Badge>
            </span>
            <span className="paragraph-sm text-fg-muted">
              {[listing.variantName, pluralize(listing.skus.length, "size")]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        </div>
        <HeaderTabs
          tabs={TABS.map((option) => ({ label: option.label }))}
          selectedTab={activeTab.label}
          onChange={(label) => {
            const next = TABS.find((option) => option.label === label)
            if (next) onTabChange(next.key)
          }}
          containerClassName="shrink-0 px-5"
        />
        <div className="flex shrink-0 flex-wrap items-center gap-2 px-5 py-3">
          <span className="paragraph-sm text-fg-subtle">Fill scope</span>
          <Select
            width={200}
            aria-label="Fill scope"
            isSearchable={false}
            options={fillScopes}
            value={fillScopes.find((option) => option.value === scope) ?? null}
            onChange={(option) => option && setScope(option.value)}
          />
          <span className="paragraph-sm text-fg-muted">
            writes to {pluralize(fillStores.length, "store")}
          </span>
          <div className="ml-auto">
            <SearchField
              initialSearchText={storeSearch}
              onSearchTextChange={setStoreSearch}
              placeholder="Search stores..."
              showInfoElement={false}
              debounceTime={150}
              containerClassName="w-56"
            />
          </div>
        </div>
        <div className="mx-5 mb-4 min-h-0 flex-1 overflow-auto rounded-lg border border-base">
          {listing.skus.length === 0 ? (
            <div className="paragraph-sm px-4 py-6 text-fg-muted">
              This variant has no sizes in the product catalogue, so there is nothing to set per
              size.
            </div>
          ) : (
            <SettingsGrid
              // A tab gets its own inputs, so text typed but not committed never carries to another field.
              key={tab}
              tab={tab}
              variantFill={
                tab !== "stores" && scope === ALL_STORES_SCOPE && storeSearch === ""
                  ? variantFillControl(tab, listing, saved, settingsDraft, allCells)
                  : undefined
              }
              listing={listing}
              sizes={listing.skus}
              stores={fillStores}
              saved={saved}
              entry={entry}
              stage={stage}
            />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-4 border-t border-base px-5 py-3">
          <span className="flex max-w-160 flex-col gap-0.5">
            <span className="paragraph-xs text-fg-subtle">{activeTab.description}</span>
            {variantChangeCount > 0 && settingsDraft.saveBlocker && (
              <span role="alert" className="paragraph-xs font-medium text-fg-warning-text">
                {settingsDraft.saveBlocker}
              </span>
            )}
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-2">
            {variantChangeCount > 0 && (
              <span className="paragraph-xs text-fg-muted">
                {pluralize(variantChangeCount, "change")} for this variant
              </span>
            )}
            <Button size="sm" variant="neutral" onClick={cancel}>
              Cancel
            </Button>
            <Tooltip title="Keeps the changes for this variant. Save them with the bar at the bottom of the page.">
              <Button size="sm" onClick={onClose}>
                Apply
              </Button>
            </Tooltip>
          </span>
        </div>
      </DialogContent>
    </DialogRoot>
  )
}
